import { supabase } from '../lib/supabase'

interface DatabaseHealthResult {
  isHealthy: boolean
  missingTables: string[]
  errors: string[]
  recommendations: string[]
}

export async function checkDatabaseHealth(): Promise<DatabaseHealthResult> {
  const result: DatabaseHealthResult = {
    isHealthy: true,
    missingTables: [],
    errors: [],
    recommendations: []
  }

  const requiredTables = [
    'ai_chat_sessions',
    'chat_messages',
    'ai_budgets',
    'budget_tracking'
  ]

  console.log('🔍 Checking database health...')

  // Check if each required table exists
  for (const tableName of requiredTables) {
    try {
      console.log(`Checking table: ${tableName}`)
      
      // Try a simple query to check if table exists
      const { error } = await supabase
        .from(tableName)
        .select('id')
        .limit(1)

      if (error) {
        console.error(`❌ Error checking ${tableName}:`, error)
        
        if (error.code === '42P01') {
          result.missingTables.push(tableName)
          result.isHealthy = false
        } else {
          result.errors.push(`${tableName}: ${error.message}`)
          result.isHealthy = false
        }
      } else {
        console.log(`✅ ${tableName} exists and is accessible`)
      }
    } catch (err) {
      console.error(`❌ Unexpected error checking ${tableName}:`, err)
      result.errors.push(`${tableName}: Unexpected error`)
      result.isHealthy = false
    }
  }

  // Generate recommendations based on findings
  if (result.missingTables.length > 0) {
    result.recommendations.push(
      '🗃️ Missing database tables detected. Run the AI budget migration:'
    )
    result.recommendations.push(
      '1. Open your Supabase dashboard → SQL Editor'
    )
    result.recommendations.push(
      '2. Copy and run the SQL from: ai_budget_migration.sql'
    )
    result.recommendations.push(
      '3. Verify tables were created successfully'
    )
  }

  if (result.errors.length > 0) {
    result.recommendations.push(
      '⚠️ Database permission or configuration issues detected'
    )
    result.recommendations.push(
      'Check your Supabase configuration and RLS policies'
    )
  }

  if (result.isHealthy) {
    result.recommendations.push('✅ Database is healthy and ready for AI chat!')
  }

  return result
}

// Quick function to test basic database connectivity
export async function testDatabaseConnection(): Promise<boolean> {
  try {
    const { error } = await supabase.auth.getUser()
    
    if (error) {
      console.error('❌ Auth check failed:', error)
      return false
    }

    console.log('✅ Database connection successful')
    return true
  } catch (err) {
    console.error('❌ Database connection failed:', err)
    return false
  }
}




